import {
  getMeanRecommendations,
  rescaleMVF,
} from "@engine/round/meanRecommender";
import { HOLD_AMOUNT, meanUserId } from "@lib/constants";
import { FailedLookupById } from "@lib/FailedLookupById";
import {
  classifyFunderRatingEvidence,
  isFunderRatingEvaluationActive,
  type FunderRatingEvidenceKind,
} from "@lib/funderRatingEvidence";
import { InvalidInputError } from "@lib/InvalidInputError";
import { scaleMvfProfile } from "@lib/matchingMvf";
import { isRichTextBlank } from "@lib/noteText";
import {
  type MVFProfile,
  mvfProfileFromTrancheProfile,
  type TrancheProfile,
} from "@lib/tranche";
import {
  type AppMode,
  type Evaluable,
  type Evaluator,
  type Track,
  trackNames,
} from "@lib/types";
import {
  algoInputPartsEqual,
  DEFAULT_FUNDER_MATCHING_BUDGET_RATIO,
  type Evaluation,
  type Funder,
  getFieldResponsesId,
  getRecommenderAlgoInputParts,
  type Node,
  type Proposal,
  type ProposalForm,
  type ProposalWithFieldResponsesId,
  type Recommender,
  type RoundAggregate,
  type RoundMember,
  type TrackStatus,
} from "./aggregate";

type TrackWithSettings = Exclude<Track, "NO_TRACK">;

export function getEvaluation(
  round: RoundAggregate,
  evaluatorId: string,
  evaluableId: string,
): Evaluation | null {
  const evaluator = getEvaluator(round, evaluatorId);
  if (!evaluator) {
    return null;
  }

  if (evaluator.type !== "recommender") {
    return null;
  }

  const evaluation = evaluator.evaluations[evaluableId];
  if (!evaluation) {
    return null;
  }

  return evaluation;
}

export function getMatchingMvfProfile(
  round: RoundAggregate,
  recommender: Recommender,
): MVFProfile {
  return scaleMvfProfile(
    recommender.mvfProfile,
    recommender.matchingMvfScales,
    getMatchingRates(round, recommender.mvfProfile),
  );
}

export function getMatchingRates(
  round: RoundAggregate,
  mvfProfile: MVFProfile,
): { [evaluableId: string]: number | undefined } {
  return Object.fromEntries(
    Object.keys(mvfProfile).map((evaluableId) => {
      const evaluable = getEvaluable(round, evaluableId);
      return [
        evaluableId,
        evaluable?.type === "proposal"
          ? evaluable.typedFields.matchRate
          : undefined,
      ];
    }),
  );
}

export function assertGetEvaluation(
  round: RoundAggregate,
  evaluatorId: string,
  evaluableId: string,
): Evaluation {
  const evaluation = getEvaluation(round, evaluatorId, evaluableId);
  if (!evaluation) {
    throw new Error(
      `Failed to find evaluation. evaluatorId: ${evaluatorId}, evaluableId: ${evaluableId}`,
    );
  }

  return evaluation;
}

export function getMemberForEvaluator(
  round: RoundAggregate,
  evaluatorId: string,
): RoundMember | null {
  return (
    Object.values(round.members).find(
      (member) => member.evaluators[evaluatorId],
    ) ?? null
  );
}

export function getEvaluator(
  round: RoundAggregate,
  evaluatorId: string,
): Recommender | Funder | null {
  for (const member of Object.values(round.members)) {
    if (member.evaluators[evaluatorId]) {
      return member.evaluators[evaluatorId];
    }
  }

  return null;
}

export function assertGetEvaluator(
  round: RoundAggregate,
  evaluatorId: string,
): Recommender | Funder {
  const evaluator = getEvaluator(round, evaluatorId);
  if (!evaluator) {
    throw new FailedLookupById("evaluator", round, evaluatorId);
  }

  return evaluator;
}

export function getRecommender(
  round: RoundAggregate,
  recommenderId: string,
): Recommender | null {
  const recommender = getEvaluator(round, recommenderId);

  if (recommender == null) return recommender;

  if (recommender.type !== "recommender") {
    throw new Error(
      `Found non-recommender evaluator when looking for a recommender, id: ${recommenderId}`,
    );
  }

  return recommender;
}

export function assertGetRecommender(
  round: RoundAggregate,
  recommenderId: string,
): Recommender {
  const recommender = assertGetEvaluator(round, recommenderId);
  if (recommender.type !== "recommender") {
    throw new Error(
      `Found non-recommender evaluator when looking for a recommender, id: ${recommenderId}`,
    );
  }

  return recommender;
}

export function getActiveRecommenderForMember(
  member: RoundMember,
): Recommender | null {
  for (const evaluator of Object.values(member.evaluators)) {
    if (evaluator.type === "recommender" && evaluator.status === "active") {
      return evaluator;
    }
  }
  return null;
}

export function getRecommenderFromUser(
  round: RoundAggregate,
  userId: string,
): Recommender | null {
  const member = round.members[userId];
  if (!member) return null;
  return getActiveRecommenderForMember(member);
}

// Unlike getRecommenderFromUser, this requires the member itself to be
// active: removed members keep their aggregate entry (status "removed") and
// must not resolve to a funder role. Matches getActiveFunders semantics for
// a single user without scanning the whole round.
export function getFunderFromUser(
  round: RoundAggregate,
  userId: string,
): Funder | null {
  const member = round.members[userId];
  if (!member || !isActive(member)) return null;
  return (
    Object.values(member.evaluators).filter(isFunder).find(isActive) ?? null
  );
}

export function getEvaluableByShortname(
  round: RoundAggregate,
  shortname: string,
): Evaluable | undefined {
  return getAllEvaluables(round).find(
    (evaluable) => evaluable.shortname === shortname,
  );
}

export function getAllEvaluables(round: RoundAggregate): Evaluable[] {
  const recommenders = getAllRecommenders(round);
  const proposals = getAllProposals(round);
  return [...recommenders, ...proposals];
}

export function getEvaluable(
  round: RoundAggregate,
  evaluableId: string,
): Evaluable | null {
  const node = getNode(round, evaluableId);
  if (!node) {
    return null;
  }

  if (node.type !== "recommender" && node.type !== "proposal") {
    return null;
  }

  return node;
}

export function getEvaluableOrPlaceholder(
  round: RoundAggregate,
  evaluableId: string,
): Evaluable {
  const evaluable = getEvaluable(round, evaluableId);
  if (evaluable) {
    return evaluable;
  }

  return getPlaceholderProposal(evaluableId);
}

function getPlaceholderProposal(
  proposalId: string,
): ProposalWithFieldResponsesId {
  return {
    id: proposalId,
    type: "proposal",
    status: "active",
    entityType: "non-profit",
    shortname: "ERR",
    lastSubmittedDatetime: new Date().toISOString(),
    firstSubmittedDatetime: new Date().toISOString(),
    requestsMatchingPledge: false,
    typedFields: {
      matchRate: 0,
      maxStandardAmount: 1e6,
      baseRequestAmount: 1e6,
      minGrantAmount: 1e6,
      maxGrantAmount: 1e6,
      receivingOrgName: "ERR",
      receivingOrgTaxId: "ERR",
      receivingOrgLocation: "ERR",
      sffApplicationId: "ERR",
      targetsFreedom: false,
      targetsFairness: false,
      longformLink: "http://example.com",
      keyIndividuals: [],
    },
    fieldResponsesId: getFieldResponsesId({}),
    longname: "ERR",
  };
}

export function getProposalOrPlaceholder(
  round: RoundAggregate,
  proposalId: string,
): ProposalWithFieldResponsesId {
  const proposal = getProposal(round, proposalId);
  if (proposal) {
    return proposal;
  }

  return getPlaceholderProposal(proposalId);
}

export function getNode(
  round: RoundAggregate,
  nodeId: string,
): Node | undefined {
  const evaluator = getEvaluator(round, nodeId);
  if (evaluator) {
    return evaluator;
  }

  return getProposal(round, nodeId);
}

export function getNodeByShortname(
  round: RoundAggregate,
  shortname: string,
): Node | undefined {
  return getAllNodes(round).find((node) => node.shortname === shortname);
}

export function assertGetEvaluable(
  round: RoundAggregate,
  evaluableId: string,
): Recommender | Proposal {
  const evaluable = getEvaluable(round, evaluableId);
  if (!evaluable) {
    throw new FailedLookupById("evaluable", round, evaluableId);
  }

  return evaluable;
}

export function getProposal(
  round: RoundAggregate,
  proposalId: string,
): ProposalWithFieldResponsesId | undefined {
  // Served from the per-proposalForms index rather than scanning every form's
  // proposals per call: this sits under getNode/getEvaluable, which hot paths
  // call once per (recommender × proposal) — the Decide matrix's exportable
  // maps and the round consistency check both showed up in profiles doing
  // thousands of these scans. The index preserves the previous
  // `getAllProposals().find()` semantics exactly (same nested iteration
  // order, first occurrence wins).
  return getProposalByIdAcrossForms(round, proposalId);
}

export function getProposalFormOfProposalId(
  round: RoundAggregate,
  proposalId: string,
): ProposalForm | undefined {
  return getAllProposalForms(round).find((proposalForm) =>
    Object.values(proposalForm.proposals).some(
      (proposal) => proposal.id === proposalId,
    ),
  );
}

export function assertGetProposalFormOfProposalId(
  round: RoundAggregate,
  proposalId: string,
): ProposalForm {
  const proposalForm = getProposalFormOfProposalId(round, proposalId);
  if (!proposalForm) {
    throw new FailedLookupById("proposalForm", round, proposalId);
  }
  return proposalForm;
}

export function assertGetProposal(
  proposalForm: ProposalForm,
  proposalId: string,
): ProposalWithFieldResponsesId {
  const proposal = proposalForm.proposals[proposalId];
  if (!proposal) {
    throw new Error(`Proposal with id ${proposalId} not found`);
  }
  return proposal;
}

export function assertGetField(proposalForm: ProposalForm, fieldId: string) {
  const field = proposalForm.fields.find((field) => field.id === fieldId);
  if (!field) {
    const fieldIds = proposalForm.fields.map((field) => field.id);
    throw new Error(
      `Field with id ${fieldId} not found. Available fields: [${fieldIds.join(", ")}]`,
    );
  }
  return field;
}

export function assertGetFunder(
  round: RoundAggregate,
  funderId: string,
): Funder {
  const evaluator = assertGetEvaluator(round, funderId);
  if (!evaluator) {
    throw new Error(`Funder not found, id: ${funderId}`);
  }

  if (evaluator.type !== "funder") {
    throw new Error(
      `Found non-funder evaluator when looking for a funder, id: ${funderId}`,
    );
  }

  return evaluator;
}

export function getAllEvaluationsForEvaluable(
  round: RoundAggregate,
  evaluableId: string,
): Evaluation[] {
  return getAllEvaluators(round).flatMap((evaluator) =>
    evaluator.type === "recommender" && evaluator.evaluations[evaluableId]
      ? [evaluator.evaluations[evaluableId]]
      : [],
  );
}

export function getAllEvaluators(round: RoundAggregate): Evaluator[] {
  return Object.values(round.members).flatMap((member) =>
    Object.values(member.evaluators),
  );
}

export function getActiveEvaluationsForEvaluable(
  round: RoundAggregate,
  evaluableId: string,
): Evaluation[] {
  const evaluable = getEvaluable(round, evaluableId);
  if (evaluable?.status !== "active") {
    return [];
  }

  if (evaluable.type !== "proposal") {
    return [];
  }

  return getActiveRecommenders(round)
    .flatMap((evaluator) => getActiveEvaluatorEvaluations(round, evaluator))
    .filter((evaluation) => evaluation.evaluableId === evaluableId);
}

// Find the evaluator ID of the evaluator who ultimately evaluates the evaluable.
export function resolveDeferredEvaluators(evaluator: Recommender): {
  [evaluableId: string]: string;
} {
  return Object.fromEntries(
    Object.entries(evaluator.evaluations)
      .filter(([_, evaluation]) => evaluation.state === "deferred")
      .map(([evaluableId, evaluation]) => [
        evaluableId,
        evaluation.deferredTo!,
      ]),
  );
}

export type MVFProfilePair = {
  standard: MVFProfile;
  matching: MVFProfile;
};

// Keyed on the recommender algo-input parts rather than the round identity:
// deferral resolution reads recommender evaluations/MVFs (own + deferred-to +
// the mean recommender's inputs), all of which those parts cover — so the
// cache survives aggregate versions whose actions didn't touch them (funder
// Decide pushes, ratings, note edits).
const mvfProfileFromDeferralsCache: {
  cacheForParts: unknown[] | null;
  cache: Map<Evaluator, MVFProfilePair>;
} = {
  cacheForParts: null,
  cache: new Map(),
};

export function mvfProfilesFromDeferrals(
  evaluator: Recommender,
  round: RoundAggregate,
): MVFProfilePair {
  const parts = getRecommenderAlgoInputParts(round);
  if (
    mvfProfileFromDeferralsCache.cacheForParts == null ||
    !algoInputPartsEqual(mvfProfileFromDeferralsCache.cacheForParts, parts)
  ) {
    mvfProfileFromDeferralsCache.cacheForParts = parts;
    mvfProfileFromDeferralsCache.cache = new Map();
  }

  const cache = mvfProfileFromDeferralsCache.cache;

  const cachedMvfProfile = cache.get(evaluator);
  if (cachedMvfProfile) {
    return cachedMvfProfile;
  }

  const mvfProfile = mvfProfileFromDeferralsInner(evaluator, round);
  cache.set(evaluator, mvfProfile);
  return mvfProfile;
}

// Build an MVF profile from a set of deferrals.
// Each deferral is a mapping from an evaluable ID to a recommender ID.
export function mvfProfileFromDeferralsInner(
  evaluator: Recommender,
  round: RoundAggregate,
): MVFProfilePair {
  const ultimateEvaluators = resolveDeferredEvaluators(evaluator);

  // ultimateEvaluators is a mapping from evaluable to recommender, we need to
  // reverse that to iterate one recommender at a time.
  const evaluablesOfRecommender: { [recommenderId: string]: Set<string> } = {};
  for (const [evaluableId, recommenderId] of Object.entries(
    ultimateEvaluators,
  )) {
    const evaluables =
      evaluablesOfRecommender[recommenderId] ?? new Set<string>();
    evaluables.add(evaluableId);
    evaluablesOfRecommender[recommenderId] = evaluables;
  }

  const evaluatorHighestFdv = Math.max(
    ...Object.values(evaluator.mvfProfile).map((mvf) => mvf[0]?.value ?? 0),
  );

  const result: MVFProfilePair = {
    standard: {},
    matching: {},
  };

  for (const [recommenderId, evaluableIds] of Object.entries(
    evaluablesOfRecommender,
  )) {
    const meanRecommender = assertGetRecommender(round, recommenderId);
    if (meanRecommender.meanRecommenderThreshold == null) {
      throw new Error(`Recommender ${recommenderId} is not a mean recommender`);
    }
    const { standard, matching } = getMeanRecommendations(
      round,
      meanRecommender.meanRecommenderThreshold,
    );

    for (const evaluableId of evaluableIds) {
      const mvf = standard[evaluableId];
      if (mvf != null) {
        // Mean recommenders' MVFs are already normalized (with highest FDV of 1),
        // so we can just multiply by the evaluator's highest FDV to make the
        // scale comparable.
        result.standard[evaluableId] = rescaleMVF(mvf, evaluatorHighestFdv);
      }

      const matchingMvf = matching[evaluableId];
      if (matchingMvf != null) {
        result.matching[evaluableId] = rescaleMVF(
          matchingMvf,
          evaluatorHighestFdv,
        );
      }
    }
  }

  return result;
}

// Keyed on the recommender algo-input parts rather than the round identity,
// so profiles survive aggregate versions whose actions didn't touch them (a
// funder's Decide pushes, ratings, note edits — building these profiles is
// the dominant cost of a cold allocation run). A FUNDER's profile derives
// from their own incremental steps (getFunderMvfProfile → tranche profile),
// which the recommender parts deliberately exclude — those entries carry the
// funder evaluator object as an extra validity tag, so any change to the
// funder's subtree rebuilds them.
const getEvaluatorMvfProfileCache: {
  cacheForParts: unknown[] | null;
  cache: Map<string, { value: MVFProfilePair; funderTag: Funder | null }>;
} = {
  cacheForParts: null,
  cache: new Map(),
};

export function getEvaluatorMvfProfile(
  round: RoundAggregate,
  evaluatorId: string,
): MVFProfile {
  return getEvaluatorMvfProfiles(round, evaluatorId).standard;
}

export function getEvaluatorMvfProfiles(
  round: RoundAggregate,
  evaluatorId: string,
): MVFProfilePair {
  const parts = getRecommenderAlgoInputParts(round);
  if (
    getEvaluatorMvfProfileCache.cacheForParts == null ||
    !algoInputPartsEqual(getEvaluatorMvfProfileCache.cacheForParts, parts)
  ) {
    getEvaluatorMvfProfileCache.cacheForParts = parts;
    getEvaluatorMvfProfileCache.cache = new Map();
  }

  const evaluator = getEvaluator(round, evaluatorId);
  const funderTag = evaluator?.type === "funder" ? evaluator : null;

  const cache = getEvaluatorMvfProfileCache.cache;
  const cached = cache.get(evaluatorId);
  if (cached && cached.funderTag === funderTag) {
    return cached.value;
  }

  const mvfProfile = getEvaluatorMvfProfilesInner(round, evaluatorId);
  cache.set(evaluatorId, { value: mvfProfile, funderTag });
  return mvfProfile;
}

// There are three cases:
// 1. The evaluator is a recommender, but they don't have any deferrals. We return their tranche list.
// 2. The evaluator is a recommender, and they have deferrals. We merge their evaluations with their deferrals.
// 3. The evaluator is a mean recommender.
// 4. The evaluator is a funder. We convert their tranche list to an MVF profile.
function getEvaluatorMvfProfilesInner(
  round: RoundAggregate,
  evaluatorId: string,
): MVFProfilePair {
  const evaluator = getEvaluator(round, evaluatorId);
  if (!evaluator) {
    throw new Error(`Evaluator not found, id: ${evaluatorId}`);
  }

  // 4: funder
  if (evaluator.type !== "recommender") {
    const funderMvfProfile = getFunderMvfProfile(round, evaluatorId);
    return {
      matching: funderMvfProfile,
      standard: funderMvfProfile,
    };
  }

  const evaluatorEvaluatedStandardMvfProfile = Object.fromEntries(
    Object.entries(evaluator.mvfProfile).filter(
      ([propId]) => evaluator.evaluations[propId]?.state === "evaluated",
    ),
  );
  const matchingMvfProfile = getMatchingMvfProfile(round, evaluator);
  const evaluatorEvaluatedMatchingMvfProfile = Object.fromEntries(
    Object.entries(matchingMvfProfile).filter(
      ([propId]) => evaluator.evaluations[propId]?.state === "evaluated",
    ),
  );

  // 1-2: non-mean-recommender
  if (evaluator?.meanRecommenderThreshold == null) {
    if (
      Object.values(evaluator.evaluations).filter(
        (evaluation) => evaluation.state === "deferred",
      ).length === 0
    ) {
      return {
        standard: evaluatorEvaluatedStandardMvfProfile,
        matching: evaluatorEvaluatedMatchingMvfProfile,
      };
    } else {
      const deferredProfiles = mvfProfilesFromDeferrals(evaluator, round);
      return {
        standard: {
          ...evaluatorEvaluatedStandardMvfProfile,
          ...deferredProfiles.standard,
        },
        matching: {
          ...evaluatorEvaluatedMatchingMvfProfile,
          ...deferredProfiles.matching,
        },
      };
    }
  }

  // 3: mean recommender
  return getMeanRecommendations(round, evaluator.meanRecommenderThreshold);
}

export function getRoundMemberForEvaluator(
  round: RoundAggregate,
  evaluatorId: string,
): RoundMember | null {
  for (const member of Object.values(round.members)) {
    if (member.evaluators[evaluatorId]) {
      return member;
    }
  }

  return null;
}

export function getRecusalForEvaluator(
  round: RoundAggregate,
  evaluatorId: string,
  evaluableId: string,
): boolean {
  return getEvaluation(round, evaluatorId, evaluableId)?.state === "recused";
}

export function getFunderRatingEvidenceKind(
  round: RoundAggregate,
  recommenderId: string,
  proposalId: string,
): FunderRatingEvidenceKind | null {
  const evaluator = getEvaluator(round, recommenderId);
  if (evaluator?.type !== "recommender" || evaluator.status !== "active") {
    return null;
  }
  const rawNote = round.members[evaluator.memberId]?.evaluableNotes[proposalId];
  return classifyFunderRatingEvidence({
    hasNote: !isRichTextBlank(rawNote),
    hasMvf: evaluator.mvfProfile[proposalId] != null,
  });
}

/**
 * The canonical membership rule for a live funder rating pair. An old manual
 * score cannot create an evaluation on its own: the recommender must still be
 * active, the evaluation must not be canceled/deferred/recused, and the pair
 * must carry a written note, an MVF, or both.
 */
export function isFunderRatingPairEligible(
  round: RoundAggregate,
  recommenderId: string,
  proposalId: string,
): boolean {
  return (
    isFunderRatingEvaluationActive(
      getEvaluation(round, recommenderId, proposalId)?.state,
    ) && getFunderRatingEvidenceKind(round, recommenderId, proposalId) != null
  );
}

/**
 * Pairs that constitute funder rating WORK: eligible AND note-bearing. A
 * no-note (MVF-only) pair is still rateable — it shows in Incremental Evals
 * with its default rating of 0 and can be overridden from the cell popover —
 * but the workspace never ASKS the funder to rate it: the Note Ratings
 * grid hides it, the todo rail's scoring progress skips it, and the
 * calibration sampler never suggests it. Every surface that counts or lists
 * "ratings the funder should enter" must use this predicate, not
 * `isFunderRatingPairEligible`, or its numbers disagree with the grid.
 */
export function isFunderRatingWorkPair(
  round: RoundAggregate,
  recommenderId: string,
  proposalId: string,
): boolean {
  const evidenceKind = getFunderRatingEvidenceKind(
    round,
    recommenderId,
    proposalId,
  );
  return (
    evidenceKind != null &&
    evidenceKind !== "mvf-only" &&
    isFunderRatingEvaluationActive(
      getEvaluation(round, recommenderId, proposalId)?.state,
    )
  );
}

export type FunderRatingPairEligibility = (
  recommenderId: string,
  proposalId: string,
) => boolean;

export function assertGetMember(
  round: RoundAggregate,
  userId: string,
): RoundMember {
  const member = round.members[userId];
  if (!member) {
    throw new FailedLookupById("member", round, userId);
  }

  return member;
}

/**
 * The identifier the proposal sync matches against the rolling application
 * spreadsheet's Round column for this form. Lives on the form; rounds created
 * before that (CREATE_ROUND v1/v2) carry a round-level value that forms added
 * back then rely on. Undefined when neither is set, which only a non-SFC round
 * or a form added without one can produce.
 */
export function getProposalFormRoundIdentifier(
  round: Pick<RoundAggregate, "roundIdentifier">,
  proposalForm: Pick<ProposalForm, "roundIdentifier">,
): string | undefined {
  const own = proposalForm.roundIdentifier?.trim();
  if (own) return own;
  const legacy = round.roundIdentifier?.trim();
  return legacy || undefined;
}

/**
 * Whether this form's proposals come from a Google spreadsheet, i.e. whether
 * the proposal sync applies to it. The round's default form (which holds the
 * hold proposal) and forms created from the Create Proposal modal have no
 * spreadsheet and never sync; nothing about them should be shown as syncable.
 */
export function isSpreadsheetBackedProposalForm(
  proposalForm: Pick<ProposalForm, "spreadsheetId">,
): boolean {
  return proposalForm.spreadsheetId.trim() !== "";
}

/** Active, spreadsheet-backed proposal forms: the ones the sync can run on. */
export function getSyncableProposalForms(
  round: RoundAggregate,
): ProposalForm[] {
  return Object.values(round.proposalForms).filter(
    (form) => form.status === "active" && isSpreadsheetBackedProposalForm(form),
  );
}

/**
 * Whether this form's proposals come from an Application Builder form
 * (ATTACH_APPLICATION_FORM). Such forms are read through the round's field
 * mapping rather than the spreadsheet sync.
 */
export function isApplicationFormBackedProposalForm(
  proposalForm: Pick<ProposalForm, "applicationForm">,
): proposalForm is ProposalForm & {
  applicationForm: NonNullable<ProposalForm["applicationForm"]>;
} {
  return proposalForm.applicationForm != null;
}

/** Active proposal forms attached from the Application Builder. */
export function getApplicationFormBackedProposalForms(
  round: RoundAggregate,
): (ProposalForm & {
  applicationForm: NonNullable<ProposalForm["applicationForm"]>;
})[] {
  return Object.values(round.proposalForms)
    .filter(isActive)
    .filter(isApplicationFormBackedProposalForm);
}

export function assertGetApplicationFormAttachment(
  round: RoundAggregate,
  proposalFormId: string,
): NonNullable<ProposalForm["applicationForm"]> {
  const proposalForm = assertGetProposalForm(round, proposalFormId);
  if (!proposalForm.applicationForm) {
    throw new Error(
      `Proposal form ${proposalFormId} ("${proposalForm.name}") is not attached from the Application Builder`,
    );
  }
  return proposalForm.applicationForm;
}

export function getAllProposalForms(round: RoundAggregate): ProposalForm[] {
  return Object.values(round.proposalForms);
}

export function getActiveProposalForms(round: RoundAggregate): ProposalForm[] {
  return Object.values(round.proposalForms).filter(isActive);
}

export function getActiveProposals(
  round: RoundAggregate,
): ProposalWithFieldResponsesId[] {
  return getActiveProposalForms(round)
    .flatMap((proposalForm) => Object.values(proposalForm.proposals))
    .filter(isActive);
}

export function getAllProposals(
  round: RoundAggregate,
): ProposalWithFieldResponsesId[] {
  return getAllProposalForms(round).flatMap((proposalForm) =>
    Object.values(proposalForm.proposals),
  );
}

function isActive(entity: { status: "active" | string }): boolean {
  return entity.status === "active";
}

function isRecommender(evaluator: Evaluator): evaluator is Recommender {
  return evaluator.type === "recommender";
}

function isFunder(evaluator: Evaluator): evaluator is Funder {
  return evaluator.type === "funder";
}

export function getAllMembers(round: RoundAggregate): RoundMember[] {
  return Object.values(round.members);
}

export function getActiveMembers(round: RoundAggregate): RoundMember[] {
  return getAllMembers(round).filter(isActive);
}

export function getAllRecommenders(round: RoundAggregate): Recommender[] {
  return getAllMembers(round)
    .flatMap((member) => Object.values(member.evaluators))
    .filter(isRecommender);
}

export type TrackRecommender = Recommender & { track: Track };

export function getActiveRecommenders(round: RoundAggregate): Recommender[] {
  return getActiveMembers(round)
    .flatMap((member) => Object.values(member.evaluators))
    .filter(isRecommender)
    .filter(isActive);
}

export function getActiveRecommendersWithTrack(
  round: RoundAggregate,
): TrackRecommender[] {
  return getActiveMembers(round).flatMap((member) =>
    Object.values(member.evaluators)
      .filter(isRecommender)
      .filter(isActive)
      .map((recommender) => ({
        ...recommender,
        track: member.track,
      })),
  );
}
export function getAllFunders(round: RoundAggregate): Funder[] {
  return getAllMembers(round)
    .flatMap((member) => Object.values(member.evaluators))
    .filter(isFunder);
}

export function getActiveFunders(round: RoundAggregate): Funder[] {
  return getActiveMembers(round)
    .flatMap((member) => Object.values(member.evaluators))
    .filter(isFunder)
    .filter(isActive);
}

export function getActiveEvaluators(round: RoundAggregate): Evaluator[] {
  return getActiveMembers(round)
    .flatMap((member) => Object.values(member.evaluators))
    .filter(isActive);
}

export function getAllNodes(
  round: RoundAggregate,
): (Recommender | Funder | ProposalWithFieldResponsesId)[] {
  const recommenders = getAllRecommenders(round);
  const funders = getAllFunders(round);
  const proposals = getAllProposals(round);

  return [...recommenders, ...funders, ...proposals];
}

export function assertShortnameIsUnique(
  round: RoundAggregate,
  shortname: string,
) {
  const conflictingNode = findShortnameConflict(round, shortname);

  if (!conflictingNode) {
    return;
  }

  console.error(
    `Shortname is not unique, shortname: ${shortname}, node: ${JSON.stringify(
      conflictingNode,
    )}`,
  );
  throw new InvalidInputError(
    `Shortname is not unique, shortname: ${shortname}`,
  );
}

export function findShortnameConflict(
  round: RoundAggregate,
  shortname: string,
): Node | undefined {
  const allNodes = getAllNodes(round);

  // An anonymized shortname is not guaranteed to be unique
  // TODO: why? it seems like that should not be the case
  const matchesTrackNames = Object.values(trackNames).some((trackName) => {
    return shortname.startsWith(`${trackName} member`);
  });

  if (matchesTrackNames) {
    return undefined;
  }

  return allNodes.find((node) => node.shortname === shortname);
}

export function isHold(round: RoundAggregate, evaluable: Evaluable): boolean {
  return round.holdProposalId === evaluable.id;
}

export function getAllRoundMemberEvaluationsForEvaluable(
  roundMember: RoundMember,
  evaluableId: string,
): Evaluation[] {
  const evaluations: Evaluation[] = [];

  for (const evaluator of Object.values(roundMember.evaluators)) {
    if (evaluator.type !== "recommender") {
      continue;
    }

    const evaluation = evaluator.evaluations[evaluableId];
    if (evaluation) {
      evaluations.push(evaluation);
    }
  }

  return evaluations;
}

export function getUnevaluatedEvaluableGroups(
  round: RoundAggregate,
  evaluator: Recommender | Funder,
): {
  recused: Evaluable[];
  notStarted: Evaluable[];
  hidden: Evaluable[];
  deferred: Evaluable[];
  evaluated: Evaluable[];
  canceled: Evaluable[];
  zeroed: Evaluable[];
} {
  const evaluables =
    evaluator.type === "recommender"
      ? getActiveProposals(round)
      : getActiveRecommenders(round);

  const recused: Evaluable[] = [];
  const deferred: Evaluable[] = [];
  const hidden: Evaluable[] = [];
  const notStarted: Evaluable[] = [];
  const evaluated: Evaluable[] = [];
  const canceled: Evaluable[] = [];
  const zeroed: Evaluable[] = [];

  if (evaluator.type === "funder") {
    return {
      recused,
      notStarted: evaluables,
      hidden,
      deferred,
      evaluated,
      canceled,
      zeroed,
    };
  }

  for (const evaluable of evaluables) {
    if (!round.isSfcRound && evaluable.id === round.holdProposalId) {
      continue;
    }

    const evaluation = evaluator.evaluations[evaluable.id];
    if (evaluation?.hidden === true) {
      hidden.push(evaluable);
    } else if (!evaluation || evaluation.state === "not-started") {
      notStarted.push(evaluable);
    } else if (evaluation.state === "evaluated") {
      if (evaluation.setToZero) {
        zeroed.push(evaluable);
      } else {
        evaluated.push(evaluable);
      }
    } else if (evaluation.state === "canceled") {
      canceled.push(evaluable);
    } else if (evaluation.state === "deferred") {
      deferred.push(evaluable);
    } else if (evaluation.state === "recused") {
      recused.push(evaluable);
    }
  }

  return {
    recused,
    notStarted,
    hidden,
    deferred,
    evaluated,
    canceled,
    zeroed,
  };
}

export function getEvaluatorActiveEvaluationIds(
  evaluator: Recommender,
): string[] {
  return Object.values(evaluator.evaluations)
    .filter((e) => e.state === "evaluated")
    .map((evaluation) => evaluation.evaluableId);
}

export function getFunderNetRecommenderAllocations(
  round: RoundAggregate,
  funderOrId: string | Funder,
): Record<string, number> {
  const funder =
    typeof funderOrId === "string"
      ? assertGetFunder(round, funderOrId)
      : funderOrId;
  const activeRecommenderIds = new Set(
    getActiveRecommenders(round).map((recommender) => recommender.id),
  );
  const netAllocations: Record<string, number> = {};

  for (const { evaluableId, incrementAmount } of funder.incrementalEvalSteps) {
    if (!activeRecommenderIds.has(evaluableId)) {
      continue;
    }

    netAllocations[evaluableId] =
      (netAllocations[evaluableId] ?? 0) + incrementAmount;
  }

  return netAllocations;
}

/** Whether this funder participates in matching. Missing is true for legacy rounds. */
export function funderUsesMatching(funder: Funder): boolean {
  return funder.usesMatching !== false;
}

/** Return the share of this funder's budget reserved for matching pledges. */
export function getFunderMatchingBudgetRatio(
  round: RoundAggregate,
  funderOrId: string | Funder,
): number {
  const funder =
    typeof funderOrId === "string"
      ? assertGetFunder(round, funderOrId)
      : funderOrId;
  if (!funderUsesMatching(funder)) {
    return 0;
  }
  if (funder.matchingBudgetRatio != null) {
    return funder.matchingBudgetRatio;
  }

  return DEFAULT_FUNDER_MATCHING_BUDGET_RATIO;
}

// Total current budget for each recommender, summed across all active funders.
// The single-funder version (getFunderNetRecommenderAllocations) is the right
// lens for "what this funder has put behind each recommender"; this is the
// round-wide truth used as the baseline for projections that must reflect every
// funder's contribution, not just the acting funder's.
export function getTotalRecommenderBudgets(
  round: RoundAggregate,
): Record<string, number> {
  const budgets: Record<string, number> = {};

  for (const funder of getActiveFunders(round)) {
    for (const [recommenderId, amount] of Object.entries(
      getFunderNetRecommenderAllocations(round, funder),
    )) {
      budgets[recommenderId] = (budgets[recommenderId] ?? 0) + amount;
    }
  }

  return budgets;
}

// Standard-grant portions of the active funders' recommender allocations.
// This deliberately applies each funder's ratio before combining their money,
// so changing one funder's split cannot alter another funder's split.
export function getCombinedStandardRecommenderAllocations(
  round: RoundAggregate,
): Record<string, number> {
  const combined: Record<string, number> = {};

  for (const funder of getActiveFunders(round)) {
    const standardShare = 1 - getFunderMatchingBudgetRatio(round, funder);
    for (const [recommenderId, amount] of Object.entries(
      getFunderNetRecommenderAllocations(round, funder),
    )) {
      combined[recommenderId] =
        (combined[recommenderId] ?? 0) + amount * standardShare;
    }
  }

  return combined;
}

// TODO: should just return set, since it is most often (always?) used as a set
export function getActiveEvaluationIds(evaluators: Recommender[]): string[] {
  const activeEvaluationIds = evaluators.flatMap((evaluator) =>
    getEvaluatorActiveEvaluationIds(evaluator),
  );

  return [...new Set(activeEvaluationIds)];
}

export function evaluatorIsMeanRecommender(evaluator: Evaluator): boolean {
  return (
    evaluator.type === "recommender" &&
    evaluator?.meanRecommenderThreshold != null
  );
}

export function userIsMeanRecommender(userId: string): boolean {
  return userId === meanUserId;
}

export function getActiveEvaluatorEvaluations(
  round: RoundAggregate,
  evaluator: Recommender,
  includeDeferred?: boolean,
): Evaluation[] {
  if (!evaluatorIsMeanRecommender(evaluator)) {
    return Object.values(evaluator.evaluations).filter(
      (evaluation) =>
        evaluation.state === "evaluated" ||
        evaluation.evaluableId === round.holdProposalId ||
        (includeDeferred && evaluation.state === "deferred"),
    );
  }

  const recommenders = getActiveRecommenders(round);
  const evaluableIds = getActiveEvaluationIds(recommenders);

  const meanRecommenderEvaluations = evaluableIds.map((evaluableId) => {
    const evaluation: Evaluation = {
      evaluableId,
      evaluableType: "proposal",
      state: "evaluated",
      hidden: false,
    };

    return evaluation;
  });

  return meanRecommenderEvaluations;
}

export function getUserShortname(
  round: RoundAggregate,
  userId: string,
): string {
  const member = round.members[userId];
  if (!member) {
    console.error(`Failed to find member, userId: ${userId}`);
    return userId;
  }

  return member.initials;
}

export function getEvaluatorShortname(
  round: RoundAggregate,
  evaluatorId: string,
): string {
  const evaluator = getEvaluator(round, evaluatorId);
  if (!evaluator) {
    return evaluatorId;
  }

  return evaluator.shortname;
}

export function assertGetEvaluatorMemberId(
  round: RoundAggregate,
  evaluatorId: string,
): string {
  const evaluator = assertGetEvaluator(round, evaluatorId);
  return evaluator.memberId;
}

// The proposal-by-id lookup is on the allocator hot path (called per proposal
// per S-Process layer rebuild), so a linear scan over every form's proposals
// per call is a real cost. Index once per proposalForms instance — the index
// reads nothing else, so it survives every aggregate version that leaves the
// forms subtree untouched.
const proposalByIdCache: {
  cacheForProposalForms: RoundAggregate["proposalForms"] | null;
  cache: Map<string, ProposalWithFieldResponsesId>;
} = {
  cacheForProposalForms: null,
  cache: new Map(),
};

function getProposalByIdAcrossForms(
  round: RoundAggregate,
  evaluableId: string,
): ProposalWithFieldResponsesId | undefined {
  if (proposalByIdCache.cacheForProposalForms !== round.proposalForms) {
    proposalByIdCache.cacheForProposalForms = round.proposalForms;
    const cache = new Map<string, ProposalWithFieldResponsesId>();
    for (const proposalForm of Object.values(round.proposalForms)) {
      for (const proposal of Object.values(proposalForm.proposals)) {
        // Keep the first occurrence, matching the previous `.find` semantics.
        if (!cache.has(proposal.id)) cache.set(proposal.id, proposal);
      }
    }
    proposalByIdCache.cache = cache;
  }
  return proposalByIdCache.cache.get(evaluableId);
}

function getEvaluableMoneyField(
  round: RoundAggregate,
  evaluableId: string,
  fieldId:
    | "minGrantAmount"
    | "maxGrantAmount"
    | "maxStandardAmount"
    | "baseRequestAmount",
): number | null {
  const evaluable = getProposalByIdAcrossForms(round, evaluableId);
  if (!evaluable) return null;

  return evaluable.typedFields[fieldId] ?? null;
}

export function getEvaluableRequestMin(
  round: RoundAggregate,
  evaluableId: string,
): number | null {
  return getEvaluableMoneyField(round, evaluableId, "minGrantAmount");
}

export function getEvaluableRequestMax(
  round: RoundAggregate,
  evaluableId: string,
): number | null {
  return getEvaluableMoneyField(round, evaluableId, "maxGrantAmount");
}

export function getEvaluableRequestMaxStandard(
  round: RoundAggregate,
  evaluableId: string,
): number | null {
  return getEvaluableMoneyField(round, evaluableId, "maxStandardAmount");
}

export function getEvaluableRequestBase(
  round: RoundAggregate,
  evaluableId: string,
): number | null {
  return getEvaluableMoneyField(round, evaluableId, "baseRequestAmount");
}

export function getProposalForm(
  round: RoundAggregate,
  proposalFormId: string,
): ProposalForm | undefined {
  return round.proposalForms[proposalFormId];
}

export function assertGetProposalForm(
  round: RoundAggregate,
  proposalFormId: string,
): ProposalForm {
  const proposalForm = round.proposalForms[proposalFormId];
  if (!proposalForm) {
    const availableIds = Object.keys(round.proposalForms);
    throw new Error(
      `Proposal form with id ${proposalFormId} not found (${availableIds.join(
        ", ",
      )})`,
    );
  }
  return proposalForm;
}

export function getProposalFormOfProposal(
  round: RoundAggregate,
  proposalId: string,
): ProposalForm | undefined {
  return Object.values(round.proposalForms).find((proposalForm) =>
    Object.values(proposalForm.proposals).some(
      (proposal) => proposal.id === proposalId,
    ),
  );
}

export function assertGetProposalFormOfProposal(
  round: RoundAggregate,
  proposalId: string,
): ProposalForm {
  const proposalForm = getProposalFormOfProposal(round, proposalId);
  if (!proposalForm) {
    throw new Error(`Proposal form not found for proposalId: ${proposalId}`);
  }
  return proposalForm;
}

export function isUserFunder(round: RoundAggregate, userId: string): boolean {
  return getActiveFunders(round).some((funder) => funder.memberId === userId);
}

export function isRestrictedFunderViewer(
  round: RoundAggregate,
  userId: string,
): boolean {
  const member = round.members[userId];
  return (
    member?.status === "active" &&
    isUserFunder(round, userId) &&
    member.isRestrictedFunder === true
  );
}

export function getHiddenRecommenderMemberIds(
  round: RoundAggregate,
  userId: string,
): ReadonlySet<string> {
  if (!isRestrictedFunderViewer(round, userId)) {
    return new Set();
  }

  const member = round.members[userId];
  return new Set(member?.hiddenRecommenderMemberIds ?? []);
}

export function isUserRecommender(
  round: RoundAggregate,
  userId: string,
): boolean {
  return getActiveRecommenders(round).some(
    (recommender) => recommender.memberId === userId,
  );
}

export function isUserRoundAdmin(
  round: RoundAggregate,
  userId: string,
): boolean {
  const member = round.members[userId];
  if (!member) {
    return false;
  }

  return member.isRoundAdmin;
}

export function isUserRoundAdminOrFunder(
  round: RoundAggregate,
  userId: string,
): boolean {
  return isUserRoundAdmin(round, userId) || isUserFunder(round, userId);
}

export function isUserRoundAdminOrThisEvaluator(
  round: RoundAggregate,
  evaluator: Recommender | Funder,
  userId: string,
) {
  return isUserRoundAdmin(round, userId) || evaluator.memberId === userId;
}

export function getHoldValue(
  mvfProfile: MVFProfile,
  holdProposalId: string,
): number {
  const mvf = mvfProfile[holdProposalId];
  if (!mvf) {
    return 0;
  }
  return mvf[0]?.value ?? 0;
}

export function getTracksWithMembers(round: RoundAggregate): Track[] {
  const tracks = new Set<Track>();
  for (const member of Object.values(round.members)) {
    tracks.add(member.track);
  }
  return Array.from(tracks);
}

/**
 * Whether this round uses tracks. When false (a "trackless" round) every
 * recommender sits in the MAIN track and track-related UI should be hidden.
 * Treats a missing flag as `true` so rounds created before the flag existed
 * keep their normal track behavior.
 */
export function roundUsesTracks(
  round: Pick<RoundAggregate, "usesTracks">,
): boolean {
  return round.usesTracks !== false;
}

/**
 * Whether the round has been archived. Archived rounds stay fully readable,
 * but the engine refuses every action except the restore itself.
 */
export function isRoundArchived(
  round: Pick<RoundAggregate, "archivedAt">,
): boolean {
  return round.archivedAt != null;
}

export function getEffectiveTrack(
  mode: Pick<AppMode, "viewAsTrackMember">,
  roundMember: RoundMember | undefined,
): Track {
  if (mode.viewAsTrackMember) {
    return mode.viewAsTrackMember;
  }

  return roundMember?.track ?? "NO_TRACK";
}

export const getTracksToShow = (
  round: RoundAggregate,
  roundMember: RoundMember | undefined,
  effectiveTrack: Track,
): TrackWithSettings[] => {
  let tracksToShow: TrackWithSettings[] = [];
  if (roundMember) {
    if (effectiveTrack === "NO_TRACK") {
      tracksToShow = getTracksWithMembers(round).filter(
        (track): track is TrackWithSettings => track !== "NO_TRACK",
      );
    } else if (
      effectiveTrack === "MAIN" ||
      effectiveTrack === "FREEDOM" ||
      effectiveTrack === "FAIRNESS"
    ) {
      tracksToShow = [effectiveTrack];
    }
  }
  return tracksToShow;
};

export const hasTrackWithMatchingStatus = (
  round: RoundAggregate,
  userId: string,
  mode: Pick<AppMode, "viewAsTrackMember">,
  accessiblePhases: string[],
) => {
  const userRoundMember = assertGetMember(round, userId);
  const effectiveTrack = getEffectiveTrack(mode, userRoundMember);

  return getTracksToShow(round, userRoundMember, effectiveTrack).some((track) =>
    accessiblePhases.includes(round.trackSettings[track].status),
  );
};

// Standard MVFs and most evaluation actions lock as soon as a track enters
// evals-freeze, and stay locked once it's complete.
export function isTrackStatusFrozen(status: TrackStatus): boolean {
  return status === "4.evals-freeze" || status === "5.complete";
}

// Matching parameters (value multiplier + drop-off rate), notes, and feedback
// stay editable through evals-freeze; they only lock once the track is marked
// complete.
export function isTrackStatusComplete(status: TrackStatus): boolean {
  return status === "5.complete";
}

// An archived round is frozen for everyone — every track, every member,
// admins included — so the round-wide lock folds into the per-member track
// predicates here. Every surface that asks "is this member's track frozen /
// complete?" then honours the archive without a second check. (The reducer
// refuses the actions regardless; this is what makes the UI agree with it.)
export function isMemberTrackFrozen(
  round: RoundAggregate,
  member: RoundMember | undefined,
) {
  if (isRoundArchived(round)) {
    return true;
  }
  if (member == null || member.track === "NO_TRACK") {
    return false;
  }

  return isTrackStatusFrozen(round.trackSettings[member.track].status);
}

export function isMemberTrackComplete(
  round: RoundAggregate,
  member: RoundMember | undefined,
) {
  if (isRoundArchived(round)) {
    return true;
  }
  if (member == null || member.track === "NO_TRACK") {
    return false;
  }

  return isTrackStatusComplete(round.trackSettings[member.track].status);
}

/**
 * Track-status edit locks for a viewer's own MVFs, in one place so every MVF
 * surface (the My MVF panel, the MVFs tab, …) gates the same way and the
 * standard-vs-matching distinction can't drift between them again.
 *
 * Round admins bypass both track locks, but not the archive: an archived
 * round is locked for everyone. Otherwise:
 * - Standard MVFs lock as soon as the viewer's track enters evals-freeze.
 * - Matching params (value multiplier + drop-off rate) stay editable through
 *   evals-freeze and only lock once the track is marked complete.
 *
 * Callers compose these with their own per-evaluation gates (whether the
 * evaluation is started, deferred, locked, hidden, or belongs to someone else).
 */
export interface ViewerMvfEditLocks {
  viewerTrackStatus: TrackStatus | undefined;
  isViewerRoundAdmin: boolean;
  isStandardMvfFrozen: boolean;
  isMatchingMvfFrozen: boolean;
}

export function getViewerMvfEditLocks(
  round: RoundAggregate,
  viewerUserId: string,
): ViewerMvfEditLocks {
  const viewer = round.members[viewerUserId];
  const viewerTrackStatus =
    viewer && viewer.track !== "NO_TRACK"
      ? round.trackSettings[viewer.track].status
      : undefined;
  const isViewerRoundAdmin = isUserRoundAdmin(round, viewerUserId);
  const archived = isRoundArchived(round);
  return {
    viewerTrackStatus,
    isViewerRoundAdmin,
    isStandardMvfFrozen:
      archived || (!isViewerRoundAdmin && isMemberTrackFrozen(round, viewer)),
    isMatchingMvfFrozen:
      archived || (!isViewerRoundAdmin && isMemberTrackComplete(round, viewer)),
  };
}

export function canEvaluatorModifyEvaluation(
  round: RoundAggregate,
  userId: string,
  evaluator?: Recommender | Funder,
  evaluable?: Evaluable,
) {
  // Nobody edits an archived round, round admins included.
  if (isRoundArchived(round)) {
    return false;
  }

  if (evaluable && evaluable.status !== "active") {
    return false;
  }

  // Mean recommender MVFs are auto-generated and should never be edited
  if (
    evaluator?.type === "recommender" &&
    evaluator.meanRecommenderThreshold != null
  ) {
    return false;
  }

  const isRoundAdmin = isUserRoundAdmin(round, userId);
  if (isRoundAdmin) {
    return true;
  }

  const user = round.members[userId];
  if (!user) {
    return false;
  }

  const isThisEvaluator = evaluator?.memberId === user.id;
  const isTrackInEvalsFreeze = isMemberTrackFrozen(round, user);

  return isRoundAdmin || (isThisEvaluator && !isTrackInEvalsFreeze);
}

export function getFunderTrancheProfile(
  round: RoundAggregate,
  funderOrId: string | Funder,
): TrancheProfile {
  const funder =
    typeof funderOrId === "string"
      ? assertGetFunder(round, funderOrId)
      : funderOrId;

  // Replay the incremental steps in chronological order, keeping each positive
  // top-up as its own tranche. The step order is the funder's click order, and
  // that is the priority the algorithm cuts from when its budget is below the
  // incremental total (e.g. the funder later lowers their budget by hand). A
  // negative step ("shift-click to remove") is applied as a LIFO reduction
  // against that recommender's most recent prior tranches rather than emitted
  // as a negative tranche. That keeps the cumulative tranche table monotone, so
  // a backed-out recommender can't leave a stale high-water row that
  // unilateralAllocInto would misallocate the budget to.
  const steps: { evaluableId: string; amount: number }[] = [];
  for (const { evaluableId, incrementAmount } of funder.incrementalEvalSteps) {
    if (incrementAmount >= 0) {
      steps.push({ evaluableId, amount: incrementAmount });
      continue;
    }
    let remaining = -incrementAmount;
    for (let i = steps.length - 1; i >= 0 && remaining > 0; i--) {
      const step = steps[i];
      if (step == null || step.evaluableId !== evaluableId) {
        continue;
      }
      const reduction = Math.min(step.amount, remaining);
      step.amount -= reduction;
      remaining -= reduction;
    }
  }

  const result: TrancheProfile = [];
  for (const { evaluableId, amount } of steps) {
    if (amount <= 0) {
      continue;
    }
    result.push({
      height: 0.001,
      allocations: {
        [evaluableId]: {
          amount,
          bottomPlaced: true,
        },
      },
    });
  }

  result.push({
    height: 1 - result.length * 0.001,
    allocations: {
      [round.holdProposalId]: {
        amount: HOLD_AMOUNT,
        bottomPlaced: true,
      },
    },
  });

  return result;
}

export function getFunderMvfProfile(
  round: RoundAggregate,
  funderOrId: string | Funder,
): MVFProfile {
  return mvfProfileFromTrancheProfile(
    getFunderTrancheProfile(round, funderOrId),
  );
}

export function getFieldResponseReferences(
  round: RoundAggregate,
): { proposalId: string; version: string }[] {
  const result: { proposalId: string; version: string }[] = [];
  for (const form of Object.values(round.proposalForms)) {
    for (const proposal of Object.values(form.proposals)) {
      result.push({
        proposalId: proposal.id,
        version: proposal.fieldResponsesId,
      });
    }
  }
  return result;
}
